import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getSocket } from "../utils/socket";
import Navbar from "./Navbar";
import { FaExpand, FaStar } from "react-icons/fa";

const Profile = () => {
  const { user: authUser, token, fetchWithRetry, isLoading, logout, error: authError } = useAuth();
  const { membershipId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();

  // Hàm tải hồ sơ
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = membershipId ? `/api/users/profile/${membershipId}` : "/api/users/profile";
      console.log("Token gửi trong fetchProfile:", token);
      const res = await fetchWithRetry({
        method: "get",
        url,
      });
      console.log("Profile response:", res.data);
      const data = res.data || {};
      if (data && data._id) {
        setUser(data);
      } else {
        console.warn("Dữ liệu từ API không hợp lệ, thiếu _id:", data);
        setError("Dữ liệu người dùng không hợp lệ từ server.");
        setUser(authUser || {});
      }
    } catch (error) {
      console.error("Lỗi khi tải hồ sơ:", error.response ? error.response.data : error.message);
      if (error.response?.status === 404) {
        setError("Người dùng không tồn tại trên server. Vui lòng kiểm tra lại tài khoản.");
        setUser(authUser || {});
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else {
        setError(error.response?.data?.message || "Không thể tải hồ sơ từ server.");
        setUser(authUser || {});
      }
    } finally {
      setLoading(false);
    }
  }, [authUser, token, fetchWithRetry, navigate, logout, membershipId]);

  // Hàm tải thông báo từ API
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetchWithRetry({
        method: "get",
        url: "/api/users/notifications",
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Lỗi khi tải thông báo:", error);
    }
  }, [fetchWithRetry]);

  // Tải hồ sơ và thông báo khi người dùng đăng nhập hoặc khi membershipId thay đổi
  useEffect(() => {
    if (isLoading) return;

    if (!authUser || !authUser._id) {
      navigate("/login");
      return;
    }

    fetchProfile();
    fetchNotifications();
  }, [authUser, isLoading, navigate, fetchProfile, fetchNotifications, membershipId]);

  // Lắng nghe thông báo từ Socket.IO
  useEffect(() => {
    if (token) {
      const socket = getSocket();
      if (!socket.connected) {
        socket.connect();
      }

      const handleNotification = (notification) => {
        console.log("Nhận thông báo từ server:", notification);
        setNotifications((prev) => {
          const isDuplicate = prev.some(
            (n) =>
              (n._id && n._id === notification._id) ||
              (!n._id &&
                n.message === notification.message &&
                n.createdAt === notification.createdAt)
          );
          if (!isDuplicate) {
            return [notification, ...prev];
          }
          return prev;
        });
      };

      socket.on("notification", handleNotification);

      return () => {
        socket.off("notification", handleNotification);
      };
    }
  }, [token]);

  const unreadCount = 0;
  const unreadNotificationCount = notifications.filter((n) => !n.read).length;

  const handleToggleNotifications = (notificationId, action) => {
    if (action === "read") {
      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
      );
    } else if (action === "delete") {
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    }
  };

  const handleEditProfileClick = () => {
    if (!authUser.permissions.includes("edit_users")) {
      setError("Bạn không có quyền chỉnh sửa hồ sơ! Vui lòng liên hệ quản trị viên.");
      return;
    }
    navigate("/edit-profile");
  };

  // Hàm đặt hình làm hình đại diện chính
  const handleSetPrimaryPhoto = async (photoUrl) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithRetry({
        method: "put",
        url: "/api/users/set-primary-photo",
        data: { photoUrl },
      });

      const newPrimaryPhoto = response.data.primaryPhoto;
      setUser((prevUser) => ({
        ...prevUser,
        primaryPhoto: newPrimaryPhoto,
      }));
      setLoading(false);
      // Re-fetch profile to ensure all data is up-to-date
      fetchProfile();
    } catch (err) {
      console.error("Lỗi khi đặt ảnh chính:", err.message);
      if (err.response?.status === 404) {
        setError("Người dùng không tồn tại trên server. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Phiên làm việc hết hạn hoặc token không hợp lệ. Vui lòng đăng nhập lại.");
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 2000);
      } else if (err.response?.status === 500) {
        setError(err.response?.data?.message || "Lỗi server khi đặt ảnh chính! Vui lòng thử lại sau.");
      } else {
        setError(err.message || "Lỗi không xác định khi đặt ảnh chính! Vui lòng thử lại.");
      }
      setLoading(false);
    }
  };

  // Hàm mở modal để xem hình phóng to
  const openImageModal = (photoUrl) => {
    setSelectedImage(photoUrl);
  };

  // Hàm đóng modal
  const closeImageModal = () => {
    setSelectedImage(null);
  };

  if (isLoading || loading) {
    return <p className="text-center text-gray-600">Đang tải...</p>;
  }

  const isOwnProfile = !membershipId || membershipId === authUser.membershipId;

  return (
    <div className="font-sans min-h-screen bg-gradient-to-b from-gray-100 to-gray-200">
      <Navbar
        unreadCount={unreadCount}
        unreadNotificationCount={unreadNotificationCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-20 max-w-5xl mx-auto p-6">
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-8">
          {isOwnProfile ? "Hồ Sơ Cá Nhân" : `Hồ Sơ của ${user?.name}`}
        </h2>

        {(error || authError) && (
          <p className="text-red-500 text-center mb-4">{error || authError}</p>
        )}

        {/* Nút điều hướng */}
        <div className="flex justify-end mb-6 space-x-4">
          <Link to="/chat" className="text-blue-600 hover:text-blue-800 font-medium transition-colors">
            Đi đến trang Chat
          </Link>
          <Link to="/search" className="text-blue-600 hover:text-blue-800 font-medium transition-colors">
            Tìm kiếm người dùng
          </Link>
          <Link to="/photo-uploader" className="text-blue-600 hover:text-blue-800 font-medium transition-colors">
            Quản lý ảnh
          </Link>
          {isOwnProfile && (
            <>
              <button
                onClick={handleEditProfileClick}
                className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                Chỉnh sửa hồ sơ
              </button>
              <Link
                to="/matching-criteria"
                className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                Cập nhật tiêu chí phù hợp
              </Link>
            </>
          )}
        </div>

        {/* Mã hội viên */}
        <div className="text-center mb-6">
          <p className="text-lg font-semibold text-gray-700">
            Mã hội viên: <span className="text-blue-600">{user?.membershipId || "Chưa có"}</span>
          </p>
        </div>

        {/* Hiển thị hình đại diện chính và các thumbnail */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative w-48 h-48 mb-4">
            <img
              src={user?.primaryPhoto?.photoUrl || "https://placehold.co/200"}
              alt="Hình đại diện chính"
              className="w-full h-full object-cover rounded-full shadow-lg border-4 border-white"
              onError={(e) => (e.target.src = "https://placehold.co/200")}
            />
            <button
              onClick={() => openImageModal(user?.primaryPhoto?.photoUrl)}
              className="absolute bottom-2 right-2 bg-gray-800 text-white p-2 rounded-full hover:bg-gray-600 transition-colors"
            >
              <FaExpand />
            </button>
          </div>

          {user?.photos && user.photos.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4">
              {user.photos.map((photo, index) => (
                <div key={index} className="relative w-24 h-24">
                  <img
                    src={photo.photoUrl}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                    onError={(e) => (e.target.src = "https://placehold.co/100")}
                    onClick={() => openImageModal(photo.photoUrl)}
                  />
                  {isOwnProfile && (
                    <button
                      onClick={() => handleSetPrimaryPhoto(photo.photoUrl)}
                      className={`absolute top-2 right-2 p-1 rounded-full transition-colors ${
                        user.primaryPhoto?.photoUrl === photo.photoUrl
                          ? "bg-yellow-500 text-white"
                          : "bg-gray-800 text-white hover:bg-yellow-500"
                      }`}
                      title="Đặt làm hình đại diện chính"
                    >
                      <FaStar />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Thông tin hồ sơ */}
        <div className="bg-white p-8 rounded-lg shadow-lg mb-8">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">Thông Tin Cá Nhân</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
            <div className="flex items-center">
              <span className="font-medium w-32">Tên:</span>
              <span>{user?.name || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Email:</span>
              <span>{user?.email || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Ngày sinh:</span>
              <span>
                {user?.dateOfBirth
                  ? new Date(user.dateOfBirth).toLocaleDateString()
                  : "Chưa cập nhật"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Giới tính:</span>
              <span>
                {user?.gender === "male" ? "Nam" :
                 user?.gender === "female" ? "Nữ" :
                 user?.gender === "other" ? "Khác" :
                 "Chưa cập nhật"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Sẵn sàng di chuyển:</span>
              <span>{user?.willingToRelocate === "yes" ? "Có" : user?.willingToRelocate === "no" ? "Không" : "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Chòm sao:</span>
              <span>{user?.zodiacSign || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Năm sinh âm lịch:</span>
              <span>{user?.lunarYear || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center col-span-2">
              <span className="font-medium w-32">Tiểu sử:</span>
              <span>{user?.bio || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Quốc gia:</span>
              <span>{user?.locationText?.country || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Thành phố:</span>
              <span>{user?.locationText?.city || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Quận/Huyện:</span>
              <span>{user?.locationText?.district || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Xã/Phường:</span>
              <span>{user?.locationText?.ward || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center col-span-2">
              <span className="font-medium w-32">Chỗ ở hiện tại:</span>
              <span>
                {user?.currentLocation?.city ? (
                  [
                    user.currentLocation.ward && `${user.currentLocation.ward}, `,
                    user.currentLocation.district && `${user.currentLocation.district}, `,
                    user.currentLocation.city && `${user.currentLocation.city}, `,
                    user.currentLocation.country,
                  ]
                    .filter(Boolean)
                    .join("")
                    .replace(/, $/, "")
                ) : (
                  "Chưa cập nhật"
                )}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Chiều cao:</span>
              <span>{user?.appearance?.height || 0} cm</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Cân nặng:</span>
              <span>{user?.appearance?.weight || 0} kg</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Màu tóc:</span>
              <span>{user?.appearance?.hairColor || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Màu mắt:</span>
              <span>{user?.appearance?.eyeColor || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Thân hình:</span>
              <span>
                {user?.appearance?.bodyType === "average" ? "Trung bình" :
                 user?.appearance?.bodyType === "slim" ? "Gầy" :
                 user?.appearance?.bodyType === "athletic" ? "Lực lưỡng" :
                 user?.appearance?.bodyType === "heavy" ? "Nặng ký" :
                 "Chưa cập nhật"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Sắc tộc:</span>
              <span>{user?.appearance?.ethnicity || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Hình xăm/Khuyên:</span>
              <span>{user?.appearance?.bodyArt || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Đánh giá diện mạo:</span>
              <span>{user?.appearance?.appearanceRating || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Uống rượu:</span>
              <span>{user?.lifestyle?.drinking === "yes" ? "Có" : user?.lifestyle?.drinking === "no" ? "Không" : user?.lifestyle?.drinking === "occasionally" ? "Thỉnh thoảng" : "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Hút thuốc:</span>
              <span>{user?.lifestyle?.smoking === "yes" ? "Có" : user?.lifestyle?.smoking === "no" ? "Không" : user?.lifestyle?.smoking === "occasionally" ? "Thỉnh thoảng" : "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Tình trạng hôn nhân:</span>
              <span>
                {user?.lifestyle?.maritalStatus === "single" ? "Độc thân" :
                 user?.lifestyle?.maritalStatus === "married" ? "Đã kết hôn" :
                 user?.lifestyle?.maritalStatus === "divorced" ? "Ly hôn" :
                 user?.lifestyle?.maritalStatus === "widowed" ? "Góa" :
                 user?.lifestyle?.maritalStatus === "other" ? "Khác" :
                 "Chưa cập nhật"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Có con:</span>
              <span>{user?.lifestyle?.hasChildren ? "Có" : "Không"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Số lượng con:</span>
              <span>{user?.lifestyle?.numberOfChildren || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Con lớn nhất:</span>
              <span>{user?.lifestyle?.oldestChild || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Con nhỏ nhất:</span>
              <span>{user?.lifestyle?.youngestChild || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Muốn có (thêm) con:</span>
              <span>{user?.lifestyle?.wantsChildren === "yes" ? "Có" : user?.lifestyle?.wantsChildren === "no" ? "Không" : user?.lifestyle?.wantsChildren === "not_sure" ? "Không chắc" : "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Có vật nuôi:</span>
              <span>{user?.lifestyle?.hasPets || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Nghề nghiệp:</span>
              <span>{user?.lifestyle?.occupation || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Thu nhập:</span>
              <span>{user?.lifestyle?.income || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Quốc tịch:</span>
              <span>{user?.culture?.nationality || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Trình độ văn hóa:</span>
              <span>{user?.culture?.education || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Ngôn ngữ chính:</span>
              <span>{user?.culture?.primaryLanguage || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Khả năng tiếng Anh:</span>
              <span>{user?.culture?.englishAbility || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Khả năng tiếng Việt:</span>
              <span>{user?.culture?.vietnameseAbility || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Ngôn ngữ:</span>
              <span>{user?.culture?.languages?.join(", ") || "Chưa cập nhật"}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium w-32">Tôn giáo:</span>
              <span>{user?.culture?.religion || "Chưa cập nhật"}</span>
            </div>
          </div>
        </div>

        {/* Bảng so sánh thông tin và tiêu chí phù hợp */}
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">So Sánh Thông Tin và Tiêu Chí Phù Hợp</h3>
          <table className="w-full text-gray-700 border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-3 text-left">Tiêu chí</th>
                <th className="border p-3 text-left">Thông tin của bạn</th>
                <th className="border p-3 text-left">Tiêu chí tìm kiếm</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border p-3">Độ tuổi</td>
                <td className="border p-3">
                  {user?.dateOfBirth
                    ? new Date().getFullYear() - new Date(user.dateOfBirth).getFullYear()
                    : "Chưa cập nhật"}
                </td>
                <td className="border p-3">
                  {user?.matchingCriteria?.ageRange?.minAge || 18} -{" "}
                  {user?.matchingCriteria?.ageRange?.maxAge || 100}
                </td>
              </tr>
              <tr>
                <td className="border p-3">Giới tính</td>
                <td className="border p-3">
                  {user?.gender === "male" ? "Nam" :
                   user?.gender === "female" ? "Nữ" :
                   user?.gender === "other" ? "Khác" :
                   "Chưa cập nhật"}
                </td>
                <td className="border p-3">
                  {user?.matchingCriteria?.gender === "male" ? "Nam" :
                   user?.matchingCriteria?.gender === "female" ? "Nữ" :
                   user?.matchingCriteria?.gender === "other" ? "Khác" :
                   user?.matchingCriteria?.gender === "any" ? "Bất kỳ" :
                   "Bất kỳ"}
                </td>
              </tr>
              <tr>
                <td className="border p-3">Sẵn sàng di chuyển</td>
                <td className="border p-3">{user?.willingToRelocate === "yes" ? "Có" : user?.willingToRelocate === "no" ? "Không" : "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.willingToRelocate === "yes" ? "Có" : user?.matchingCriteria?.willingToRelocate === "no" ? "Không" : "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Quốc gia</td>
                <td className="border p-3">{user?.locationText?.country || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.locationText?.country || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Thành phố</td>
                <td className="border p-3">{user?.locationText?.city || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.locationText?.city || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Quận/Huyện</td>
                <td className="border p-3">{user?.locationText?.district || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.locationText?.district || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Xã/Phường</td>
                <td className="border p-3">{user?.locationText?.ward || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.locationText?.ward || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Chỗ ở hiện tại</td>
                <td className="border p-3">
                  {user?.currentLocation?.city ? (
                    [
                      user.currentLocation.ward && `${user.currentLocation.ward}, `,
                      user.currentLocation.district && `${user.currentLocation.district}, `,
                      user.currentLocation.city && `${user.currentLocation.city}, `,
                      user.currentLocation.country,
                    ]
                      .filter(Boolean)
                      .join("")
                      .replace(/, $/, "")
                  ) : (
                    "Chưa cập nhật"
                  )}
                </td>
                <td className="border p-3">
                  {user?.matchingCriteria?.currentLocation?.city ? (
                    [
                      user.matchingCriteria.currentLocation.ward && `${user.matchingCriteria.currentLocation.ward}, `,
                      user.matchingCriteria.currentLocation.district && `${user.matchingCriteria.currentLocation.district}, `,
                      user.matchingCriteria.currentLocation.city && `${user.matchingCriteria.currentLocation.city}, `,
                      user.matchingCriteria.currentLocation.country,
                    ]
                      .filter(Boolean)
                      .join("")
                      .replace(/, $/, "")
                  ) : (
                    "Bất kỳ"
                  )}
                </td>
              </tr>
              <tr>
                <td className="border p-3">Chiều cao</td>
                <td className="border p-3">{user?.appearance?.height || 0} cm</td>
                <td className="border p-3">
                  {user?.matchingCriteria?.appearance?.height?.min || 0} -{" "}
                  {user?.matchingCriteria?.appearance?.height?.max || 300} cm
                </td>
              </tr>
              <tr>
                <td className="border p-3">Cân nặng</td>
                <td className="border p-3">{user?.appearance?.weight || 0} kg</td>
                <td className="border p-3">
                  {user?.matchingCriteria?.appearance?.weight?.min || 0} -{" "}
                  {user?.matchingCriteria?.appearance?.weight?.max || 300} kg
                </td>
              </tr>
              <tr>
                <td className="border p-3">Màu tóc</td>
                <td className="border p-3">{user?.appearance?.hairColor || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.appearance?.hairColor || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Màu mắt</td>
                <td className="border p-3">{user?.appearance?.eyeColor || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.appearance?.eyeColor || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Thân hình</td>
                <td className="border p-3">
                  {user?.appearance?.bodyType === "average" ? "Trung bình" :
                   user?.appearance?.bodyType === "slim" ? "Gầy" :
                   user?.appearance?.bodyType === "athletic" ? "Lực lưỡng" :
                   user?.appearance?.bodyType === "heavy" ? "Nặng ký" :
                   "Chưa cập nhật"}
                </td>
                <td className="border p-3">
                  {user?.matchingCriteria?.appearance?.bodyType === "average" ? "Trung bình" :
                   user?.matchingCriteria?.appearance?.bodyType === "slim" ? "Gầy" :
                   user?.matchingCriteria?.appearance?.bodyType === "athletic" ? "Lực lưỡng" :
                   user?.matchingCriteria?.appearance?.bodyType === "heavy" ? "Nặng ký" :
                   user?.matchingCriteria?.appearance?.bodyType === "any" ? "Bất kỳ" :
                   "Bất kỳ"}
                </td>
              </tr>
              <tr>
                <td className="border p-3">Sắc tộc</td>
                <td className="border p-3">{user?.appearance?.ethnicity || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.appearance?.ethnicity || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Hình xăm/Khuyên</td>
                <td className="border p-3">{user?.appearance?.bodyArt || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.appearance?.bodyArt || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Đánh giá diện mạo</td>
                <td className="border p-3">{user?.appearance?.appearanceRating || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.appearance?.appearanceRating || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Uống rượu</td>
                <td className="border p-3">{user?.lifestyle?.drinking === "yes" ? "Có" : user?.lifestyle?.drinking === "no" ? "Không" : user?.lifestyle?.drinking === "occasionally" ? "Thỉnh thoảng" : "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.drinking === "yes" ? "Có" : user?.matchingCriteria?.lifestyle?.drinking === "no" ? "Không" : user?.matchingCriteria?.lifestyle?.drinking === "occasionally" ? "Thỉnh thoảng" : "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Hút thuốc</td>
                <td className="border p-3">{user?.lifestyle?.smoking === "yes" ? "Có" : user?.lifestyle?.smoking === "no" ? "Không" : user?.lifestyle?.smoking === "occasionally" ? "Thỉnh thoảng" : "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.smoking === "yes" ? "Có" : user?.matchingCriteria?.lifestyle?.smoking === "no" ? "Không" : user?.matchingCriteria?.lifestyle?.smoking === "occasionally" ? "Thỉnh thoảng" : "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Tình trạng hôn nhân</td>
                <td className="border p-3">
                  {user?.lifestyle?.maritalStatus === "single" ? "Độc thân" :
                   user?.lifestyle?.maritalStatus === "married" ? "Đã kết hôn" :
                   user?.lifestyle?.maritalStatus === "divorced" ? "Ly hôn" :
                   user?.lifestyle?.maritalStatus === "widowed" ? "Góa" :
                   user?.lifestyle?.maritalStatus === "other" ? "Khác" :
                   "Chưa cập nhật"}
                </td>
                <td className="border p-3">
                  {user?.matchingCriteria?.lifestyle?.maritalStatus === "single" ? "Độc thân" :
                   user?.matchingCriteria?.lifestyle?.maritalStatus === "married" ? "Đã kết hôn" :
                   user?.matchingCriteria?.lifestyle?.maritalStatus === "divorced" ? "Ly hôn" :
                   user?.matchingCriteria?.lifestyle?.maritalStatus === "widowed" ? "Góa" :
                   user?.matchingCriteria?.lifestyle?.maritalStatus === "other" ? "Khác" :
                   user?.matchingCriteria?.lifestyle?.maritalStatus === "any" ? "Bất kỳ" :
                   "Bất kỳ"}
                </td>
              </tr>
              <tr>
                <td className="border p-3">Có con</td>
                <td className="border p-3">{user?.lifestyle?.hasChildren ? "Có" : "Không"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.hasChildren === "yes" ? "Có" : user?.matchingCriteria?.lifestyle?.hasChildren === "no" ? "Không" : "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Số lượng con</td>
                <td className="border p-3">{user?.lifestyle?.numberOfChildren || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.numberOfChildren || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Con lớn nhất</td>
                <td className="border p-3">{user?.lifestyle?.oldestChild || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.oldestChild || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Con nhỏ nhất</td>
                <td className="border p-3">{user?.lifestyle?.youngestChild || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.youngestChild || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Muốn có (thêm) con</td>
                <td className="border p-3">{user?.lifestyle?.wantsChildren === "yes" ? "Có" : user?.lifestyle?.wantsChildren === "no" ? "Không" : user?.lifestyle?.wantsChildren === "not_sure" ? "Không chắc" : "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.wantsChildren === "yes" ? "Có" : user?.matchingCriteria?.lifestyle?.wantsChildren === "no" ? "Không" : user?.matchingCriteria?.lifestyle?.wantsChildren === "not_sure" ? "Không chắc" : "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Có vật nuôi</td>
                <td className="border p-3">{user?.lifestyle?.hasPets || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.hasPets || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Nghề nghiệp</td>
                <td className="border p-3">{user?.lifestyle?.occupation || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lifestyle?.occupation || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Quốc tịch</td>
                <td className="border p-3">{user?.culture?.nationality || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.culture?.nationality || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Trình độ văn hóa</td>
                <td className="border p-3">{user?.culture?.education || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.culture?.education || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Ngôn ngữ chính</td>
                <td className="border p-3">{user?.culture?.primaryLanguage || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.culture?.primaryLanguage || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Khả năng tiếng Anh</td>
                <td className="border p-3">{user?.culture?.englishAbility || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.culture?.englishAbility || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Khả năng tiếng Việt</td>
                <td className="border p-3">{user?.culture?.vietnameseAbility || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.culture?.vietnameseAbility || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Tôn giáo</td>
                <td className="border p-3">{user?.culture?.religion || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.culture?.religion || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Chòm sao</td>
                <td className="border p-3">{user?.zodiacSign || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.zodiacSign || "Bất kỳ"}</td>
              </tr>
              <tr>
                <td className="border p-3">Năm sinh âm lịch</td>
                <td className="border p-3">{user?.lunarYear || "Chưa cập nhật"}</td>
                <td className="border p-3">{user?.matchingCriteria?.lunarYear || "Bất kỳ"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal để xem hình phóng to */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={closeImageModal}
        >
          <div className="relative max-w-3xl max-h-[80vh]">
            <img
              src={selectedImage}
              alt="Hình phóng to"
              className="w-full h-full object-contain rounded-lg"
              onError={(e) => (e.target.src = "https://placehold.co/600")}
            />
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 bg-gray-800 text-white p-2 rounded-full hover:bg-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;